// Provider Templates API Service
// Handles fetching provider templates (seeded data)

import { apiFetch } from "./apiFetch";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type GenerationType = "text" | "image" | "audio" | "video";
export type Modality = "text" | "image" | "audio" | "video" | "embedding";

export interface CredentialField {
	name: string;
	label: string;
	type: "text" | "password" | "url" | "select";
	required: boolean;
	default?: string;
	placeholder?: string;
	helpText?: string;
	options?: { value: string; label: string }[];
}

export interface DefaultModel {
	id: string;
	name: string;
	type: string; // "text", "image", "audio", "video", "embedding"
	contextWindow?: number;
	supportsVision?: boolean;
	description?: string;
}

export interface ProviderFeatures {
	streaming?: boolean;
	functionCalling?: boolean;
	vision?: boolean;
	embeddings?: boolean;
	localDeployment?: boolean;
}

export interface ProviderTemplate {
	id: string;
	name: string;
	identifier: string;
	description: string | null;
	logoUrl: string | null;
	supportedModalities: Modality[];
	generationTypes: GenerationType[];
	defaultEndpoints: Record<string, string>;
	credentialFields: CredentialField[];
	features: ProviderFeatures | null;
	defaultModels: DefaultModel[] | null;
	isHostedAvailable: boolean;
	hostedPricing: { inputPer1M?: number; outputPer1M?: number } | null;
	isActive: boolean;
	sortOrder: number;
}

export interface ProviderWithModels extends ProviderTemplate {
	models: DefaultModel[];
}

// ═══════════════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * List all provider templates
 */
export async function listProviderTemplates(filters?: {
	generationType?: GenerationType;
	isActive?: boolean;
	isHostedAvailable?: boolean;
}): Promise<ProviderTemplate[]> {
	const params = new URLSearchParams();
	if (filters?.generationType)
		params.set("generationType", filters.generationType);
	if (filters?.isActive !== undefined)
		params.set("isActive", String(filters.isActive));
	if (filters?.isHostedAvailable !== undefined)
		params.set("isHostedAvailable", String(filters.isHostedAvailable));

	const query = params.toString();
	return apiFetch<ProviderTemplate[]>(
		`/provider-templates${query ? `?${query}` : ""}`,
	);
}

/**
 * Get provider templates by generation type
 */
export async function listProvidersByType(
	type: GenerationType,
): Promise<ProviderTemplate[]> {
	return apiFetch<ProviderTemplate[]>(`/provider-templates/by-type/${type}`);
}

/**
 * Get providers with their models for a generation type
 * Useful for model selection UI
 */
export async function listProvidersWithModels(
	type: GenerationType,
): Promise<ProviderWithModels[]> {
	return apiFetch<ProviderWithModels[]>(
		`/provider-templates/with-models/${type}`,
	);
}

/**
 * Get a single provider template by identifier
 */
export async function getProviderTemplate(
	identifier: string,
): Promise<ProviderTemplate> {
	return apiFetch<ProviderTemplate>(`/provider-templates/${identifier}`);
}

/**
 * Get provider template with full model list
 */
export async function getProviderWithModels(
	identifier: string,
): Promise<ProviderWithModels> {
	return apiFetch<ProviderWithModels>(
		`/provider-templates/${identifier}/models`,
	);
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get modality display name
 */
export function getModalityLabel(modality: Modality): string {
	const labels: Record<Modality, string> = {
		text: "Text Generation",
		image: "Image Generation",
		audio: "Audio Generation",
		video: "Video Generation",
		embedding: "Embeddings",
	};
	return labels[modality] || modality;
}

/**
 * Get modality icon name (for use with icon components)
 */
export function getModalityIcon(modality: Modality): string {
	const icons: Record<Modality, string> = {
		text: "MessageSquare",
		image: "Image",
		audio: "Volume2",
		video: "Video",
		embedding: "Database",
	};
	return icons[modality] || "Layers";
}

/**
 * Filter models by type
 */
export function filterModelsByType(
	models: DefaultModel[],
	type: string,
): DefaultModel[] {
	return models.filter((m) => m.type === type);
}

/**
 * Check if provider supports a specific modality
 */
export function providerSupportsModality(
	provider: ProviderTemplate,
	modality: Modality,
): boolean {
	return provider.supportedModalities.includes(modality);
}
